import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, EmptyState } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useDeleteProductMutation, useProductQuery, useUpdateProductMutation } from '../api';
import { BulkEditGrid } from '../components/BulkEditGrid';
import { FeaturedToggle } from '../components/FeaturedToggle';
import { ImageUploader } from '../components/ImageUploader';
import { OptionBuilder } from '../components/OptionBuilder';
import { ProductForm } from '../components/ProductForm';
import { ProductImageGallery } from '../components/ProductImageGallery';
import { StockStateBadge } from '../components/StockStateBadge';
import { VariantGeneratorWizard } from '../components/VariantGeneratorWizard';
import { VariantList } from '../components/VariantList';
import { VariantOptionsReviewBanner } from '../components/VariantOptionsReviewBanner';
import type { ProductFormValues } from '../schemas';

import { type ProductDetailLocationState } from './product-detail-location-state';

import { NextStepBanner } from '@/components/ui/NextStepBanner';
import { FormSkeleton } from '@/components/ui/skeletons/FormSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';

export function ProductDetailPage(): ReactElement {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const productQuery = useProductQuery(id);
  const updateMutation = useUpdateProductMutation(id ?? '');
  const deleteMutation = useDeleteProductMutation();
  const [formError, setFormError] = useState<string | null>(null);

  // `location.state` carries this ONLY on the navigation ProductCreatePage
  // performed right after a successful creation — a direct link, a refresh,
  // or coming back here later all arrive with no state, so the banner is
  // never resurfaced outside the one moment it is meant for.
  const locationState = location.state as ProductDetailLocationState | null;
  const [showNextStep, setShowNextStep] = useState(locationState?.showInventoryNextStep === true);

  async function onSubmit(values: ProductFormValues): Promise<void> {
    setFormError(null);
    try {
      await updateMutation.mutateAsync(values);
      toast.success(t('merchant.products.updatedToast'));
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  async function onDelete(): Promise<void> {
    if (id === undefined) return;
    if (!window.confirm(t('merchant.products.deleteConfirm'))) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t('merchant.products.deletedToast'));
      void navigate('/products');
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  if (id === undefined) {
    return <EmptyState title={t('merchant.products.notFoundTitle')} />;
  }

  if (productQuery.isPending) {
    return (
      <div className="mx-auto max-w-3xl">
        <FormSkeleton fields={8} />
      </div>
    );
  }

  if (productQuery.isError) {
    return (
      <EmptyState
        title={t('merchant.products.detailLoadError')}
        description={t(resolveErrorMessageKey(productQuery.error))}
      />
    );
  }

  const product = productQuery.data;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      {showNextStep ? (
        <NextStepBanner
          title={t('merchant.products.nextStep.title')}
          description={t('merchant.products.nextStep.description')}
          ctaLabel={t('merchant.products.nextStep.cta')}
          to="/inventory"
          onDismiss={() => {
            setShowNextStep(false);
          }}
        />
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="sk-page-title">{product.name}</h1>
          <p className="font-mono text-xs text-inkFaint">{product.sku}</p>
        </div>
        <div className="flex items-center gap-3">
          <StockStateBadge stock={product.stock} />
          <Button
            variant="danger"
            size="sm"
            loading={deleteMutation.isPending}
            onClick={() => {
              void onDelete();
            }}
          >
            {t('merchant.common.delete')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-sm text-inkSoft">{t('merchant.products.featured.hint')}</p>
          {product.featuredAt !== null && product.status !== 'active' ? (
            <p className="text-sm text-warn">{t('merchant.products.featured.inactiveNote')}</p>
          ) : null}
        </div>
        <FeaturedToggle product={product} variant="button" />
      </div>

      {formError !== null ? (
        <p role="alert" className="rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
          {formError}
        </p>
      ) : null}

      <ProductForm
        defaultValues={{
          categoryId: product.categoryId,
          name: product.name,
          slug: product.slug,
          shortDescription: product.shortDescription,
          longDescription: product.longDescription,
          sku: product.sku,
          price: product.price,
          compareAtPrice: product.compareAtPrice,
          brand: product.brand,
          status: product.status,
          seoTitle: product.seoTitle,
          seoDescription: product.seoDescription,
        }}
        onSubmit={(values) => {
          void onSubmit(values);
        }}
        isSubmitting={updateMutation.isPending}
        submitLabel={t('merchant.store.saveSubmit')}
      />

      <VariantOptionsReviewBanner
        productId={product.id}
        variantOptionsReviewedAt={product.variantOptionsReviewedAt}
      />

      <OptionBuilder productId={product.id} />

      <div className="flex flex-wrap items-center gap-2">
        <VariantGeneratorWizard productId={product.id} productSku={product.sku} />
        <BulkEditGrid productId={product.id} />
      </div>

      <VariantList productId={product.id} />

      <div className="flex flex-col gap-3">
        <h2 className="font-display text-lg font-semibold text-ink">
          {t('merchant.products.imagesHeading')}
        </h2>
        <ProductImageGallery productId={product.id} />
        <ImageUploader
          productId={product.id}
          onUploaded={() => {
            toast.success(t('merchant.products.imageUploaded'));
          }}
        />
      </div>
    </div>
  );
}
